import cn from 'classnames';
import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { FullHeadMPRProjection } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useMedia } from '@/shared/hooks';
import { Checkbox } from '@/shared/ui';

import {
  MPRToolsBar,
  useAddFullHeadMprViewToReport,
  useMPRCommands,
  useMPRShortcuts,
  type FullHeadMprReportSelectionResolver,
} from '@/features/MPR';

import {
  FULL_HEAD_MPR_SESSION_PROJECTIONS,
  FULL_HEAD_MPR_TOOLS,
  type FullHeadMprSliceGroupView,
  type FullHeadMprSliceRecordViewsByTileID,
  type FullHeadMprSliceTileView,
  type ToothSliceGeometry,
  type ToothSliceOptionsBySeries,
} from '../../config';
import {
  useFullHeadMPRTools,
  useFullHeadMprEditor,
  useFullHeadMprSliceSession,
} from '../../hooks';
import { resolveFullHeadMprSeriesTile } from '../../lib';
import viewerStyles from '../FullHeadMPR.module.scss';
import { FullHeadMPRPaneControls } from '../FullHeadMPRPaneControls';
import { FullHeadMprSliceSessionPane } from '../FullHeadMprSliceSessionPane';
import { FullHeadMPRStatus } from '../FullHeadMPRStatus';

import styles from './FullHeadMprSliceSession.module.scss';

type FullHeadMprSliceSessionProps = {
  group: FullHeadMprSliceGroupView | null;
  input: ToothSliceGeometry | null;
  recordsByTileID: FullHeadMprSliceRecordViewsByTileID;
  sliceOptions: ToothSliceOptionsBySeries;
  tile: FullHeadMprSliceTileView;
  toothID: string;
  className?: string;
  groups?: FullHeadMprSliceGroupView[];
  isSingleProjection?: boolean;
  renderLayout: (parts: {
    children: ReactNode;
    contentProps: ReturnType<typeof useMPRShortcuts>;
    actions?: ReactNode;
    controls?: ReactNode;
    order?: number;
    shortcutsControl?: ReactNode;
    renderToolbar?: (className: string) => ReactNode;
  }) => ReactNode;
  onPrimaryProjectionChange: (projection: FullHeadMPRProjection | null) => void;
  resolveViewSelection?: FullHeadMprReportSelectionResolver;
};

export const FullHeadMprSliceSession: FC<FullHeadMprSliceSessionProps> = (
  props,
) => {
  const {
    recordsByTileID,
    className,
    isSingleProjection = false,
    renderLayout,
    onPrimaryProjectionChange,
    resolveViewSelection,
    ...params
  } = props;
  const { isMobile } = useMedia();
  const isImageToolbar = isSingleProjection && !isMobile;
  const containerRef = useRef<HTMLDivElement>(null);
  const seriesProjection =
    params.tile.seriesName === 'frontal' ? 'coronal' : params.tile.seriesName;
  const sourceProjection =
    params.tile.kind === 'view'
      ? params.tile.view.projection
      : seriesProjection;
  const visibleProjections = isSingleProjection
    ? [sourceProjection]
    : FULL_HEAD_MPR_SESSION_PROJECTIONS;
  const state = useFullHeadMprSliceSession({
    ...params,
    containerRef,
    record: recordsByTileID[params.tile.id],
    visibleProjections,
  });
  const { session } = state;
  const activeProjection = state.activePane?.projection;

  useEffect(() => {
    if (!session) {
      onPrimaryProjectionChange(null);
      return;
    }

    const update = () =>
      onPrimaryProjectionChange(session.getPrimaryProjection());

    update();

    return session.addEventListener('fullHeadMPRPaneLayoutChanged', update);
  }, [session, onPrimaryProjectionChange]);

  const editor = useFullHeadMprEditor({
    isReady: state.isReady,
    original: state.original,
    session,
    toothID: params.toothID,
  });
  const tools = useFullHeadMPRTools({
    canEditAnnotations: editor.canEditAnnotations,
    canEditImageOptions: editor.canEditImageOptions,
    isReady: state.isReady,
    onReset: state.reset,
    session,
  });
  const commands = useMPRCommands({
    ...tools,
    screen: 'toothPage',
    tools: FULL_HEAD_MPR_TOOLS,
    toothID: params.toothID,
    isDisabled: !state.isReady,
    isDownloadDisabled: editor.isDownloadDisabled,
    onAxesVisibleChange: tools.setAxesVisible,
    onDownload: editor.download,
    onToolClick: tools.handleToolClick,
    onZoomSyncedChange: tools.setZoomSynced,
    onStepSlice: session
      ? (direction) => session.stepSlice(direction)
      : undefined,
  });
  const shortcuts = useMPRShortcuts({
    commands,
    isEnabled: state.isReady && !editor.isCheckingSignature,
  });
  const snapshot = activeProjection ? editor.snapshots[activeProjection] : null;
  const currentTile =
    isSingleProjection && snapshot
      ? resolveFullHeadMprSeriesTile({
          groups: params.groups ?? (params.group ? [params.group] : []),
          input: params.input,
          snapshot,
        })
      : null;
  const add = useAddFullHeadMprViewToReport({
    toothID: params.toothID,
    volumeFileID: state.original?.view.volumeFileID ?? null,
    original: state.original,
    getSnapshot: state.getSnapshot,
    resolveSelection: resolveViewSelection,
    snapshot,
  });
  return (
    <MPRToolsBar
      {...tools}
      screen="toothPage"
      tools={FULL_HEAD_MPR_TOOLS}
      commands={commands}
      shortcutsEnabled
      toothID={params.toothID}
      variant={isImageToolbar ? 'medicalImage' : 'default'}
      isDisabled={!state.isReady}
      paneLayout={!isSingleProjection && session ? session : undefined}
      onToolClick={tools.handleToolClick}
      onAxesVisibleChange={tools.setAxesVisible}
      onZoomSyncedChange={tools.setZoomSynced}
      renderLayout={({
        annotationsControl,
        controls,
        shortcutsControl,
        renderToolbar,
      }) => {
        const renderSessionToolbar = (toolbarClassName?: string) =>
          renderToolbar({
            className: toolbarClassName,
          });

        return renderLayout({
          contentProps: shortcuts,
          controls,
          order: currentTile ? currentTile.order + 1 : undefined,
          shortcutsControl,
          renderToolbar: isImageToolbar ? renderSessionToolbar : undefined,
          actions: isSingleProjection && (
            <>
              <Checkbox
                checked={add.isSelected}
                disabled={
                  add.isLoading || add.isDisabled || !state.isActiveViewReady
                }
                onChange={add.onToggleSelection}
              >
                <FormattedMessage
                  id="medicalImages.Selected"
                  defaultMessage="Selected"
                />
              </Checkbox>
              {activeProjection &&
                editor.hasAnnotationsByProjection[activeProjection] &&
                annotationsControl}
            </>
          ),
          children: (
            <div
              className={className}
              data-component="full-head-mpr-slice-session"
            >
              {!isImageToolbar && renderSessionToolbar()}
              <div className={styles.container}>
                <div
                  ref={containerRef}
                  data-mpr-viewport
                  className={cn(
                    styles.viewer,
                    !state.isReady && viewerStyles.preparing,
                  )}
                />
                <FullHeadMPRStatus
                  errorMessage={state.errorMessage}
                  isReady={state.isReady}
                  onRetry={state.retry}
                />
              </div>
              <FullHeadMPRPaneControls
                isReady={Boolean(session)}
                session={session}
              />
              {editor.panes.map((pane) => (
                <FullHeadMprSliceSessionPane
                  key={pane.projection}
                  pane={pane}
                  addToReport={
                    state.isReady
                      ? add.getAddToReportProps({
                          isViewReady: pane.isReady,
                          snapshot: pane.snapshot,
                        })
                      : undefined
                  }
                  isAnnotationsHidden={!tools.annotations.isVisible}
                />
              ))}
            </div>
          ),
        });
      }}
    />
  );
};
